import { maybe } from '@passionware/monads';
import {
  Booking,
  Resource,
  EntityType,
  RateInterval,
  BookingType
} from '../../../__types__';
import type { BookingFromHttp } from './booking.api.http.schema';

/**
 * Convert API booking response to Booking type
 * Date conversion is now handled by the schema preprocessing
 * Adds required fields that may not be present in API response
 *
 * Used after wire-level list parsing (e.g. `rowLevelValidation.unsafeMapAll` in `booking.api.http.ts`).
 */
export function convertApiBookingToBooking(
  apiBooking: BookingFromHttp
): Booking {
  return {
    ...apiBooking,
    entity_type: EntityType.BOOKING,
    contact_id: maybe.getOrUndefined(apiBooking.contact_id),
    status_id: maybe.getOrUndefined(apiBooking.status_id),
    contact_rate_id: maybe.getOrUndefined(apiBooking.contact_rate_id),
    rate_id: maybe.getOrUndefined(apiBooking.rate_id),
    rate_interval: maybe.getOrUndefined(
      apiBooking.rate_interval as unknown as RateInterval
    ),
    rate_amount: maybe.getOrUndefined(apiBooking.rate_amount),
    rate_agency_fee: maybe.getOrUndefined(apiBooking.rate_agency_fee),
    rate_payroll_fee: maybe.getOrUndefined(apiBooking.rate_payroll_fee),
    rate_processing_fee: maybe.getOrUndefined(apiBooking.rate_processing_fee),
    submitted_date: maybe.getOrUndefined(apiBooking.submitted_date),
    agreed_date: maybe.getOrUndefined(apiBooking.agreed_date),
    cancelled_at: maybe.getOrUndefined(apiBooking.cancelled_at),
    confirmed_at: maybe.getOrUndefined(apiBooking.confirmed_at),
    pencilled_at: maybe.getOrUndefined(apiBooking.pencilled_at),
    confirmation_sent_at: maybe.getOrUndefined(apiBooking.confirmation_sent_at),
    updated_at: maybe.getOrNull(apiBooking.updated_at),
    licence: maybe.getOrUndefined(apiBooking.licence),
    note: maybe.getOrUndefined(apiBooking.note),
    guid: maybe.getOrElse(apiBooking.guid, ''),
    resource_slot_assignments: maybe.getOrElse(
      apiBooking.resource_slot_assignments,
      []
    ),
    created_mechanism: maybe.getOrElse(apiBooking.created_mechanism, null),
    contact: maybe.getOrUndefined(apiBooking.contact) as unknown as Resource,
    rate_currency: maybe.getOrUndefined(apiBooking.rate_currency),
    // Add required fields from base types
    custom_fields: maybe.getOrElse(apiBooking.custom_fields, []),
    custom_fieldgroups: [], // Not provided by API, default to empty
    booking_type: {
      ...apiBooking.booking_type,
      icon: maybe.getOrUndefined(apiBooking.booking_type.icon),
      model: maybe.getOrUndefined(apiBooking.booking_type.model)
    } as unknown as BookingType
  };
}
